import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Space, Row, Col, Statistic, Descriptions,
    message, Popconfirm, Typography, Tabs, Empty, Modal, Input, Spin, Form, Select
} from 'antd';
import {
    ArrowLeftOutlined, UserOutlined, TeamOutlined, DollarOutlined,
    CheckCircleOutlined, ClockCircleOutlined, StopOutlined, CopyOutlined,
    PhoneOutlined, MailOutlined, CalendarOutlined, PlusOutlined,
    LinkOutlined, DeleteOutlined, SearchOutlined, UserAddOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import citiesService from '../../common/services/cities.service';
import dayjs from 'dayjs';
import './affiliates.scss';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const DetailAffiliatePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    // Modal pour lier un chauffeur
    const [linkModalVisible, setLinkModalVisible] = useState(false);
    const [availableDrivers, setAvailableDrivers] = useState([]);
    const [driversLoading, setDriversLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [linking, setLinking] = useState(false);

    // Modal pour créer un nouveau chauffeur
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [creating, setCreating] = useState(false);
    const [cities, setCities] = useState([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await affiliatesService.getAffiliateById(id);
            if (response.success) {
                setData(response.data);
            } else {
                message.error('Affilié non trouvé');
                navigate('/dashboard/affiliates');
            }
        } catch (error) {
            console.error('Erreur chargement:', error);
            message.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        citiesService.getAll().then(res => {
            if (res?.data) setCities(res.data.filter(c => c.isActive));
        }).catch(() => {});
    }, [id]);

    // Recherche des chauffeurs disponibles (filtrés par la ville de l'affilié)
    const searchAvailableDrivers = useCallback(async (search = '') => {
        setDriversLoading(true);
        try {
            const params = { search, limit: 20 };
            const affiliateCityId = data?.affiliate?.city?._id || data?.affiliate?.city;
            if (affiliateCityId) {
                params.cityId = affiliateCityId;
            }
            const response = await affiliatesService.getAvailableDriversForAffiliate(params);
            if (response.success) {
                setAvailableDrivers(response.data || []);
            }
        } catch (error) {
            console.error('Erreur recherche chauffeurs:', error);
            message.error('Erreur lors de la recherche des chauffeurs');
        } finally {
            setDriversLoading(false);
        }
    }, [data]);

    // Ouvrir le modal et charger les chauffeurs
    const openLinkModal = () => {
        setLinkModalVisible(true);
        setSearchTerm('');
        searchAvailableDrivers('');
    };

    // Ouvrir le modal de création
    const openCreateModal = () => {
        form.resetFields();
        setCreateModalVisible(true);
    };

    // Recherche avec debounce
    useEffect(() => {
        if (linkModalVisible) {
            const timer = setTimeout(() => {
                searchAvailableDrivers(searchTerm);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [searchTerm, linkModalVisible, searchAvailableDrivers]);

    // Lier un chauffeur à l'affilié
    const handleLinkDriver = async (driverId) => {
        setLinking(true);
        try {
            const response = await affiliatesService.linkDriverToAffiliate(id, driverId);
            if (response.success) {
                message.success(response.message || 'Chauffeur lié avec succès');
                setLinkModalVisible(false);
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de la liaison');
            }
        } catch (error) {
            console.error('Erreur liaison:', error);
            message.error(error.response?.data?.message || 'Erreur lors de la liaison du chauffeur');
        } finally {
            setLinking(false);
        }
    };

    // Créer un nouveau chauffeur
    const handleCreateDriver = async (values) => {
        setCreating(true);
        try {
            // Ajouter le préfixe +225 au numéro de téléphone
            const dataToSend = {
                ...values,
                phone: `+225${values.phone.replace(/\s/g, '')}`
            };
            const response = await affiliatesService.createDriverForAffiliate(id, dataToSend);
            if (response.success) {
                message.success(
                    <span>
                        Chauffeur <strong>{response.data.driver.firstName} {response.data.driver.lastName}</strong> créé avec succès !
                        <br />
                        <small>Mot de passe par défaut : <code>123456</code> | Points : 2000</small>
                    </span>,
                    5
                );
                setCreateModalVisible(false);
                form.resetFields();
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de la création');
            }
        } catch (error) {
            console.error('Erreur création:', error);
            message.error(error.response?.data?.message || 'Erreur lors de la création du chauffeur');
        } finally {
            setCreating(false);
        }
    };

    // Délier un chauffeur de l'affilié
    const handleUnlinkDriver = async (driverId) => {
        try {
            const response = await affiliatesService.unlinkDriverFromAffiliate(id, driverId);
            if (response.success) {
                message.success('Chauffeur délié avec succès');
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de la suppression de la liaison');
            }
        } catch (error) {
            console.error('Erreur déliaison:', error);
            message.error(error.response?.data?.message || 'Erreur lors de la déliaison');
        }
    };

    const handleSuspend = async () => {
        try {
            const response = await affiliatesService.suspendAffiliate(id);
            if (response.success) {
                message.success('Affilié suspendu');
                fetchData();
            }
        } catch (error) {
            message.error('Erreur lors de la suspension');
        }
    };

    const handleReactivate = async () => {
        try {
            const response = await affiliatesService.reactivateAffiliate(id);
            if (response.success) {
                message.success('Affilié réactivé');
                fetchData();
            }
        } catch (error) {
            message.error('Erreur lors de la réactivation');
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        message.success('Code copié !');
    };

    const getStatusTag = (status) => {
        const statusConfig = {
            approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Approuvé' },
            pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'En attente' },
            suspended: { color: 'error', icon: <StopOutlined />, text: 'Suspendu' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
    };

    const driversColumns = [
        {
            title: 'Nom',
            key: 'name',
            render: (_, record) => `${record.firstName || ''} ${record.lastName || ''}`
        },
        {
            title: 'Téléphone',
            dataIndex: 'phone',
            key: 'phone'
        },
        {
            title: 'Date parrainage',
            key: 'referredAt',
            render: (_, record) => dayjs(record.referredBy?.referredAt || record.createdAt).format('DD/MM/YYYY HH:mm')
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Popconfirm
                    title="Délier ce chauffeur de l'affilié ?"
                    description="Le chauffeur ne sera plus associé à cet affilié."
                    onConfirm={() => handleUnlinkDriver(record._id)}
                    okText="Oui, délier"
                    cancelText="Annuler"
                    okButtonProps={{ danger: true }}
                >
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                    />
                </Popconfirm>
            )
        }
    ];

    const commissionsColumns = [
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                const types = {
                    referral_bonus: 'Bonus parrainage',
                    activity_bonus: 'Bonus activité',
                    monthly_bonus: 'Bonus mensuel',
                    special_bonus: 'Bonus spécial'
                };
                return types[type] || type;
            }
        },
        {
            title: 'Montant',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => <Text strong>{amount?.toLocaleString()} FCFA</Text>
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const statusConfig = {
                    pending: { color: 'warning', text: 'En attente' },
                    approved: { color: 'processing', text: 'Approuvé' },
                    paid: { color: 'success', text: 'Payé' },
                    cancelled: { color: 'error', text: 'Annulé' }
                };
                const config = statusConfig[status] || statusConfig.pending;
                return <Tag color={config.color}>{config.text}</Tag>;
            }
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('DD/MM/YYYY')
        }
    ];

    // Colonnes pour les chauffeurs disponibles dans le modal
    const availableDriversColumns = [
        {
            title: 'Nom',
            key: 'name',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>
                        {record.firstName || ''} {record.lastName || ''}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Inscrit le {dayjs(record.createdAt).format('DD/MM/YYYY')}
                    </Text>
                </div>
            )
        },
        {
            title: 'Téléphone',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone) => <Tag color="blue">{phone}</Tag>
        },
        {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<LinkOutlined />}
                    loading={linking}
                    onClick={() => handleLinkDriver(record._id)}
                    style={{ borderRadius: 6 }}
                >
                    Lier
                </Button>
            )
        }
    ];

    if (loading) {
        return <Card loading={true} />;
    }

    if (!data) {
        return <Empty description="Affilié non trouvé" />;
    }

    const { affiliate, referredDrivers, commissions, stats } = data;

    return (
        <div className="affiliate-detail-page">
            <div className="page-header">
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/affiliates')}
                    >
                        Retour
                    </Button>
                    <Title level={3} style={{ margin: 0 }}>
                        Détail Affilié
                    </Title>
                </Space>
                <Space>
                    {affiliate.affiliateProfile?.status === 'approved' && (
                        <Popconfirm
                            title="Suspendre cet affilié ?"
                            onConfirm={handleSuspend}
                            okText="Oui"
                            cancelText="Non"
                        >
                            <Button danger>Suspendre</Button>
                        </Popconfirm>
                    )}
                    {affiliate.affiliateProfile?.status === 'suspended' && (
                        <Popconfirm
                            title="Réactiver cet affilié ?"
                            onConfirm={handleReactivate}
                            okText="Oui"
                            cancelText="Non"
                        >
                            <Button type="primary">Réactiver</Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>

            {/* Infos affilié */}
            <Card className="info-card">
                <Descriptions title="Informations" bordered column={{ xs: 1, sm: 2, md: 3 }}>
                    <Descriptions.Item label={<><UserOutlined /> Nom</>}>
                        {affiliate.firstName} {affiliate.lastName}
                    </Descriptions.Item>
                    <Descriptions.Item label={<><PhoneOutlined /> Téléphone</>}>
                        {affiliate.phone}
                    </Descriptions.Item>
                    <Descriptions.Item label={<><MailOutlined /> Email</>}>
                        {affiliate.email || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ville">
                        {affiliate.city?.name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Statut">
                        {getStatusTag(affiliate.affiliateProfile?.status)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<><CalendarOutlined /> Approuvé le</>}>
                        {affiliate.affiliateProfile?.approvedAt
                            ? dayjs(affiliate.affiliateProfile.approvedAt).format('DD/MM/YYYY')
                            : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Rôle">
                        {affiliate.role === 'chauffeur' && affiliate.affiliateProfile?.status ? (
                            <><Tag color="blue">chauffeur</Tag><Tag color="orange">affilié</Tag></>
                        ) : affiliate.affiliateProfile?.status ? (
                            <Tag color="orange">affilié</Tag>
                        ) : (
                            <Tag color="blue">{affiliate.role}</Tag>
                        )}
                    </Descriptions.Item>
                </Descriptions>

                <div className="referral-code-box">
                    <div className="code">
                        {affiliate.affiliateProfile?.referralCode}
                        <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => copyCode(affiliate.affiliateProfile?.referralCode)}
                            style={{ marginLeft: 8 }}
                        />
                    </div>
                    <Text type="secondary">Code de parrainage</Text>
                </div>
            </Card>

            {/* Statistiques */}
            <Row gutter={16} className="stats-row">
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title="Chauffeurs parrainés"
                            value={stats?.totalReferred || 0}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title="Chauffeurs actifs"
                            value={stats?.activeDrivers || 0}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title="Commissions payées"
                            value={stats?.totalCommissions || 0}
                            suffix="FCFA"
                            prefix={<DollarOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <Statistic
                            title="Commissions en attente"
                            value={stats?.pendingCommissions || 0}
                            suffix="FCFA"
                            valueStyle={{ color: '#faad14' }}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Tabs */}
            <Card>
                <Tabs defaultActiveKey="drivers">
                    <TabPane
                        tab={`Chauffeurs parrainés (${referredDrivers?.length || 0})`}
                        key="drivers"
                    >
                        {/* Boutons d'action */}
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <Button
                                icon={<LinkOutlined />}
                                onClick={openLinkModal}
                                style={{ borderRadius: 8 }}
                            >
                                Lier un chauffeur existant
                            </Button>
                            <Button
                                type="primary"
                                icon={<UserAddOutlined />}
                                onClick={openCreateModal}
                                style={{
                                    borderRadius: 8,
                                    background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
                                    border: 'none'
                                }}
                            >
                                Créer un chauffeur
                            </Button>
                        </div>

                        <Table
                            columns={driversColumns}
                            dataSource={referredDrivers}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            locale={{ emptyText: 'Aucun chauffeur parrainé' }}
                        />
                    </TabPane>
                    <TabPane tab={`Commissions (${commissions?.length || 0})`} key="commissions">
                        <Table
                            columns={commissionsColumns}
                            dataSource={commissions}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            locale={{ emptyText: 'Aucune commission' }}
                        />
                    </TabPane>
                </Tabs>
            </Card>

            {/* Modal pour lier un chauffeur existant */}
            <Modal
                title={
                    <Space>
                        <LinkOutlined style={{ color: '#1890ff' }} />
                        <span>Lier un chauffeur existant à {affiliate.firstName} {affiliate.lastName}</span>
                    </Space>
                }
                open={linkModalVisible}
                onCancel={() => setLinkModalVisible(false)}
                footer={null}
                width={700}
            >
                <div style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Rechercher par nom ou téléphone..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                        size="large"
                        style={{ borderRadius: 8 }}
                    />
                </div>

                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Sélectionnez un chauffeur à lier à cet affilié. Seuls les chauffeurs non parrainés sont affichés.
                </Text>

                {driversLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin tip="Recherche des chauffeurs..." />
                    </div>
                ) : (
                    <Table
                        columns={availableDriversColumns}
                        dataSource={availableDrivers}
                        rowKey="_id"
                        pagination={{ pageSize: 5 }}
                        locale={{ emptyText: 'Aucun chauffeur disponible trouvé' }}
                        size="small"
                    />
                )}
            </Modal>

            {/* Modal pour créer un nouveau chauffeur */}
            <Modal
                title={
                    <Space>
                        <UserAddOutlined style={{ color: '#52c41a' }} />
                        <span>Créer un chauffeur pour {affiliate.firstName} {affiliate.lastName}</span>
                    </Space>
                }
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                footer={null}
                width={500}
            >
                <div style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 20
                }}>
                    <Text type="secondary">
                        Le chauffeur sera automatiquement lié à cet affilié avec :
                    </Text>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                        <li><strong>2000 points</strong> de bienvenue</li>
                        <li>Mot de passe par défaut : <code>123456</code></li>
                        <li>Commission de parrainage pour l'affilié</li>
                    </ul>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateDriver}
                >
                    <Form.Item
                        name="lastName"
                        label="Nom"
                        rules={[{ required: true, message: 'Le nom est requis' }]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Ex: Dupont"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="firstName"
                        label="Prénom"
                        rules={[{ required: true, message: 'Le prénom est requis' }]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Ex: Jean"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label="Téléphone"
                        rules={[
                            { required: true, message: 'Le téléphone est requis' },
                            { pattern: /^[\d\s]{8,}$/, message: 'Numéro de téléphone invalide (10 chiffres)' }
                        ]}
                    >
                        <Input
                            addonBefore="+225"
                            placeholder="07 00 00 00 00"
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="city"
                        label="Ville"
                        rules={[{ required: true, message: 'La ville est requise' }]}
                    >
                        <Select
                            placeholder="Sélectionner la ville du chauffeur"
                            size="large"
                            style={{ borderRadius: 8 }}
                            suffixIcon={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />}
                        >
                            {cities.map(city => (
                                <Select.Option key={city._id} value={city._id}>
                                    {city.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="driverProfilId"
                        label="ID Profil Yandex (optionnel)"
                    >
                        <Input
                            placeholder="Ex: abc123def456..."
                            size="large"
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                        <Row justify="end" gutter={12}>
                            <Col>
                                <Button onClick={() => setCreateModalVisible(false)}>
                                    Annuler
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={creating}
                                    icon={<UserAddOutlined />}
                                    style={{
                                        borderRadius: 8,
                                        background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
                                        border: 'none'
                                    }}
                                >
                                    Créer le chauffeur
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DetailAffiliatePage;
